/**
 * Enhanced Physical/Athletic Intelligence Report Generator
 * Generates detailed physical test analysis reports
 */

import { calculateComponentScores } from './physicalComponentScores';

export function generateEnhancedPhysicalReport(personalInfo, physicalTests) {
  if (!physicalTests || physicalTests.length === 0) {
    return {
      studentName: `${personalInfo.firstName} ${personalInfo.lastName}`,
      school: personalInfo.schoolName,
      grade: personalInfo.grade,
      section: personalInfo.section,
      reportType: 'Athletic Intelligence Report',
      testsCompleted: 0,
      message: 'No physical tests completed yet. Complete at least 4 tests to generate a comprehensive report.'
    };
  }

  const age = personalInfo.age || 15;
  const sport = personalInfo.sport || 'Athletics';

  // Generate individual test analyses
  const testAnalyses = physicalTests.map(test => generateTestAnalysis(test, age, sport));

  // Calculate overall score
  const overallScore = calculatePhysicalScore(testAnalyses);
  
  // Calculate component scores (Strength, Endurance, Flexibility, Balance)
  const componentScores = calculateComponentScores(physicalTests, testAnalyses);

  return {
    // Header
    studentName: `${personalInfo.firstName} ${personalInfo.lastName}`,
    school: personalInfo.schoolName,
    grade: personalInfo.grade,
    section: personalInfo.section,
    age,
    sport,
    reportType: 'Athletic Intelligence Report',

    // Overall Summary
    overallScore,
    testsCompleted: physicalTests.length,
    
    // Component Scores (4 fitness domains)
    componentScores,

    // Individual Test Analyses
    testAnalyses,

    // 90-Day Prediction
    prediction90Day: {
      icon: '🏆',
      message: 'With consistent training and focused intensity and strength, you can expect to see significant improvements in your performance, bringing you closer to your athletic goals.'
    },

    // Coach's Message
    coachMessage: {
      icon: '🏆',
      message: 'Stay committed to your training, and embrace the journey! Every small improvement counts towards your long-term goal. Keep pushing your limits!'
    }
  };
}

function generateTestAnalysis(test, age, sport) {
  // Map test IDs to proper names
  const testNameMap = {
    'squat': 'Squats',
    'lunge': 'Lunges',
    'tpose': 'T-Pose Balance',
    'highknee': 'High Knee March',
    'plank': 'Plank Hold',
    'pushup': 'Push-ups',
    'forwardbend': 'Forward Bend'
  };
  
  const testName = testNameMap[test.testId] || test.testName || test.testId;
  const score = calculateTestScore(test);

  // Get test-specific analysis
  const analysis = getTestSpecificAnalysis(test, age, sport);

  return {
    testName,
    testId: test.testId,
    score,
    reps: test.reps,
    duration: test.duration,
    metrics: test.metrics,
    
    // Header section
    areasOfImprovement: analysis.areasOfImprovement,
    suggestions: analysis.suggestions,
    potentialRisks: analysis.potentialRisks,

    // Main analysis
    title: analysis.title,
    
    // Science column
    science: {
      explanation: analysis.science.explanation,
      toDoFix: analysis.science.toDoFix,
      drill: analysis.science.drill
    },

    // Sport column
    sport: {
      application: analysis.sport.application,
      upgrade90Day: analysis.sport.upgrade90Day,
      upgrade14Day: analysis.sport.upgrade14Day,
      tech: analysis.sport.tech
    }
  };
}

function calculateTestScore(test) {
  // Calculate score based on test type and performance
  if (test.type === 'reps') {
    const completion = (test.reps / test.targetReps) * 100;
    return Math.min(100, Math.round(completion));
  } else if (test.type === 'time') {
    const completion = (test.duration / test.targetDuration) * 100;
    return Math.min(100, Math.round(completion));
  }
  return 70; // Default score
}

function getTestSpecificAnalysis(test, age, sport) {
  const testId = test.testId;

  // Squat Analysis
  if (testId === 'squat') {
    return {
      areasOfImprovement: [
        'Hip and knee coordination',
        'Core stability throughout the movement',
        'Depth and range of motion'
      ],
      suggestions: [
        'Practice deep squats with bodyweight',
        'Strength training for glutes and quads',
        'Mobility work for hips and ankles'
      ],
      potentialRisks: [
        'Knee valgus (knees caving in) can lead to ACL injuries',
        'Lower back strain from poor form',
        'Ankle mobility limitations'
      ],
      title: 'Unlock Your Squat Power! 💪',
      science: {
        explanation: 'The squat is a fundamental movement pattern that tests lower body power, quad/glute strength, and mobility. ' + (test.reps >= test.targetReps ? 'Your performance shows good lower body strength and control.' : 'Improving your squat will enhance explosive movements, jumping ability, and acceleration in ' + sport + '.') + ' Proper squat form requires hip flexion, knee flexion, and ankle dorsiflexion working in coordination. Strong quads, glutes, and core are essential for maintaining proper alignment and generating force.',
        toDoFix: `Perform dynamic squats (bodyweight, goblet, or jump squats) 3x per week. Focus on: 1) Depth - aim for thighs parallel to ground or lower, 2) Knee tracking - keep knees aligned with toes, not caving inward, 3) Core engagement - maintain neutral spine throughout. Start with 3 sets of 10-15 reps, progressively adding weight or difficulty. Include mobility work: hip flexor stretches, ankle mobility drills, and foam rolling for quads/glutes.`,
        drill: `Goblet Squat Drill: Hold a weight (dumbbell or kettlebell) at chest level. Stand with feet shoulder-width apart, toes slightly out. Descend slowly (3 seconds), keeping chest up and weight in heels. Go as deep as possible while maintaining form. Pause at bottom (1 second), then drive up explosively (1 second). Perform 3 sets of 12-15 reps, 2-3x per week. This builds strength, reinforces proper form, and improves mobility.`
      },
      sport: {
        application: 'Powerful squats translate to improved agility and speed on the court or field. ' + (sport === 'Football' ? 'In football, strong squats enhance your ability to accelerate, change direction quickly, and win physical battles.' : 'Strong lower body power from squats improves jumping, sprinting, and explosive movements in your sport.') + ' Athletes with good squat strength can generate more force, move more efficiently, and reduce injury risk during explosive movements. Better squat mechanics also improve landing mechanics, crucial for injury prevention.',
        upgrade90Day: `90-Day Squat Progression: Week 1-4: Build baseline with bodyweight squats (3x15 daily) and goblet squats (3x12, 3x/week). Focus on form and depth. Week 5-8: Add resistance - barbell back squats (3x8-10, 2x/week) and jump squats (3x8, 2x/week). Increase weight by 5-10% weekly. Week 9-12: Peak strength - heavy squats (4x5-6, 2x/week) and plyometric variations (box jumps, depth jumps). Test max reps at end. Track: depth, weight, reps, and form quality. Expect 30-50% strength increase and improved explosive power.`,
        upgrade14Day: `14-Day Squat Intensive: Days 1-7: Daily bodyweight squats (3x20) focusing on perfect form and full depth. Add goblet squats (3x12) every other day. Include hip and ankle mobility work (10 min daily). Days 8-14: Increase intensity - weighted squats (3x10-12) 3x this week. Add jump squats (3x8) 2x this week. Continue mobility work. Focus on explosive upward movement. You'll notice improved depth, stability, and power within 2 weeks. Track daily progress and adjust based on recovery.`,
        tech: `Use MediaPipe or video analysis to track your squat form. Record yourself from the side and front. Check: 1) Depth - are thighs at least parallel? 2) Knee tracking - do knees stay aligned with toes? 3) Back angle - is spine neutral? 4) Weight distribution - is weight in heels? Compare videos weekly to see improvements. Use apps like Coach's Eye or Hudl Technique for slow-motion analysis. Set angle markers to measure depth and knee tracking objectively.`
      }
    };
  }

  // Lunge Analysis
  if (testId === 'lunge') {
    return {
      areasOfImprovement: [
        'Single-leg strength and balance',
        'Hip stability and control',
        'Coordination between legs'
      ],
      suggestions: [
        'Practice walking lunges for balance',
        'Single-leg exercises (step-ups, Bulgarian split squats)',
        'Hip strengthening exercises'
      ],
      potentialRisks: [
        'Knee instability if front knee caves inward',
        'Hip flexor tightness limiting range',
        'Balance issues leading to poor form'
      ],
      title: 'Master Your Lunge Technique! 🦵',
      science: {
        explanation: 'Lunges test single-leg strength, balance, and hip stability. ' + (test.reps >= test.targetReps ? 'Your performance demonstrates good unilateral strength and control.' : 'Improving lunges will enhance your ability to change direction, decelerate, and perform lateral movements in ' + sport + '.') + ' Lunges require coordination between the front and back leg, with the front leg bearing most of the load. Strong glutes, quads, and hip stabilizers are crucial for proper form and injury prevention.',
        toDoFix: `Perform lunges 3x per week: walking lunges (3x10 each leg), reverse lunges (3x12 each leg), and lateral lunges (3x10 each leg). Focus on: 1) Front knee tracking over toes, not past, 2) Back knee lowering to near ground, 3) Upright torso throughout, 4) Controlled descent and explosive rise. Start with bodyweight, progress to holding dumbbells. Include hip flexor stretches and glute activation exercises.`,
        drill: `Walking Lunge Drill: Start standing, step forward with right leg into deep lunge. Front knee at 90°, back knee near ground. Push through front heel to bring back leg forward into next lunge. Alternate legs for 20 total lunges (10 each leg). Focus on balance and control. Perform 3 sets, 3x per week. Progress by holding dumbbells or adding a twist at the bottom for core engagement.`
      },
      sport: {
        application: 'Strong lunges improve change of direction, deceleration, and lateral movements crucial in ' + sport + '. ' + (sport === 'Football' ? 'In football, lunge strength helps you cut sharply, defend laterally, and maintain balance during physical contact.' : 'Lunge strength enhances your ability to move multi-directionally and maintain stability during dynamic movements.') + ' Athletes with good lunge mechanics can decelerate safely, change direction explosively, and reduce knee injury risk.',
        upgrade90Day: `90-Day Lunge Progression: Week 1-4: Build foundation with bodyweight walking lunges (3x10 each leg, 3x/week) and reverse lunges (3x12 each leg, 2x/week). Week 5-8: Add resistance - dumbbell lunges (3x10 each leg, 3x/week) and Bulgarian split squats (3x8 each leg, 2x/week). Increase weight by 5 lbs weekly. Week 9-12: Advanced variations - jumping lunges (3x8 each leg, 2x/week) and lateral lunges with weight (3x10 each leg, 2x/week). Expect significant improvements in single-leg strength and balance.`,
        upgrade14Day: `14-Day Lunge Intensive: Days 1-7: Daily walking lunges (3x10 each leg) focusing on perfect form and balance. Add reverse lunges (3x12 each leg) every other day. Include hip flexor stretches (5 min daily). Days 8-14: Increase difficulty - weighted lunges (3x10 each leg) 3x this week. Add jumping lunges (3x6 each leg) 2x this week for power. Continue stretching. You'll notice improved balance, strength, and control within 2 weeks.`,
        tech: `Record lunges from the side and front. Check: 1) Front knee angle - is it 90°? 2) Back knee depth - does it nearly touch ground? 3) Torso position - is it upright? 4) Balance - is movement controlled? Use video analysis to compare left vs right leg and identify imbalances. Track progress weekly. Apps like Coach's Eye help analyze form in slow motion.`
      }
    };
  }

  // T-Pose Balance Analysis
  if (testId === 'tpose') {
    return {
      areasOfImprovement: [
        'Core stability and balance',
        'Shoulder stability and alignment',
        'Proprioception and body awareness'
      ],
      suggestions: [
        'Single-leg balance exercises',
        'Core strengthening (planks, dead bugs)',
        'Shoulder stability exercises'
      ],
      potentialRisks: [
        'Ankle instability leading to falls',
        'Poor core control affecting balance',
        'Shoulder fatigue from extended hold'
      ],
      title: 'Enhance Your Balance & Stability! 🤸',
      science: {
        explanation: 'The T-Pose balance test assesses core stability, proprioception, and overall body control. ' + (test.duration >= test.targetDuration ? 'Your performance shows excellent balance and core control.' : 'Improving balance will enhance your stability during dynamic movements and reduce injury risk in ' + sport + '.') + ' Balance requires integration of visual, vestibular, and proprioceptive systems. Strong core, ankle stability, and body awareness are essential for maintaining balance during athletic movements.',
        toDoFix: `Practice balance exercises daily: single-leg stands (3x30 seconds each leg), T-pose holds (3x30 seconds), and balance board work (5 min). Progress by: 1) Closing eyes, 2) Adding arm movements, 3) Standing on unstable surfaces (foam pad, BOSU ball), 4) Adding sport-specific movements. Include core work: planks (3x45 seconds), side planks (3x30 seconds each side), and dead bugs (3x10 each side).`,
        drill: `Single-Leg Balance Progression: Start with eyes open, stand on one leg for 30 seconds. Maintain T-pose arms. Progress to: eyes closed (30 sec), head turns (30 sec), catching/throwing ball (30 sec), standing on foam pad (30 sec). Perform 3 sets each leg, daily. This builds proprioception, ankle stability, and core control essential for athletic performance.`
      },
      sport: {
        application: 'Superior balance translates to better body control, stability during contact, and injury prevention in ' + sport + '. ' + (sport === 'Football' ? 'In football, balance helps you maintain control during tackles, change direction without losing footing, and stay upright in physical battles.' : 'Balance is crucial for maintaining control during dynamic movements, landing safely, and preventing ankle/knee injuries.') + ' Athletes with good balance can react quickly to perturbations and maintain performance under fatigue.',
        upgrade90Day: `90-Day Balance Progression: Week 1-4: Daily single-leg stands (3x30 sec each leg, eyes open). Add T-pose holds (3x30 sec, 3x/week). Week 5-8: Progress to eyes closed (3x30 sec each leg, daily). Add unstable surface work - foam pad or BOSU ball (3x20 sec, 3x/week). Week 9-12: Sport-specific balance - single-leg catches (3x10 each leg, 3x/week), balance with resistance bands (3x15 sec, 2x/week). Expect dramatic improvements in stability and body control.`,
        upgrade14Day: `14-Day Balance Intensive: Days 1-7: Daily single-leg balance (3x30 sec each leg, eyes open). Add T-pose holds (3x30 sec) every other day. Include ankle mobility work (5 min daily). Days 8-14: Progress to eyes closed (3x30 sec each leg, daily). Add dynamic balance - single-leg reaches (3x10 each leg, 3x this week). Continue ankle work. You'll notice improved stability and confidence within 2 weeks.`,
        tech: `Use video to assess balance quality. Record from front and side. Check: 1) Body alignment - is spine neutral? 2) Ankle stability - does ankle wobble? 3) Arm position - are arms steady at T-pose? 4) Duration - how long can you hold? Track progress weekly. Consider using balance assessment apps or force plates if available to measure sway and stability objectively.`
      }
    };
  }

  // Push-up Analysis
  if (testId === 'pushup') {
    return {
      areasOfImprovement: [
        'Upper body strength and endurance',
        'Core stability during movement',
        'Shoulder and elbow alignment'
      ],
      suggestions: [
        'Progressive push-up variations',
        'Shoulder strengthening exercises',
        'Core stability work'
      ],
      potentialRisks: [
        'Shoulder impingement from poor form',
        'Lower back sag indicating weak core',
        'Wrist strain from improper hand position'
      ],
      title: 'Build Your Upper Body Power! 💪',
      science: {
        explanation: 'Push-ups test upper body strength, core stability, and shoulder health. ' + (test.reps >= test.targetReps ? 'Your performance demonstrates good upper body strength and control.' : 'Improving push-ups will enhance your upper body endurance, core stability, and shoulder health for ' + sport + '.') + ' Push-ups require coordinated activation of chest, shoulders, triceps, and core. Proper form maintains a straight line from head to heels, engaging the entire kinetic chain.',
        toDoFix: `Perform push-ups 3x per week: standard push-ups (3x max reps), incline push-ups (3x15), and diamond push-ups (3x8). Focus on: 1) Body alignment - straight line from head to heels, 2) Elbow position - 45° from body, not flared, 3) Full range - chest to ground, arms fully extended, 4) Controlled tempo - 2 sec down, 1 sec up. If struggling, start with incline push-ups (hands on bench) and progress to floor.`,
        drill: `Push-Up Progression Drill: Week 1-2: Incline push-ups (hands on bench, 3x12). Week 3-4: Standard push-ups (3x8-10). Week 5-6: Diamond push-ups (3x6-8). Week 7-8: Plyometric push-ups (3x5). Perform 3x per week. Focus on perfect form at each level before progressing. This builds strength progressively while maintaining proper mechanics.`
      },
      sport: {
        application: 'Strong push-ups improve upper body endurance, core stability, and shoulder health crucial for ' + sport + '. ' + (sport === 'Football' ? 'In football, upper body strength helps in shielding the ball, physical battles, and throw-ins.' : 'Upper body strength enhances your ability to maintain posture, absorb contact, and generate power in your sport.') + ' Athletes with good push-up strength have better shoulder stability, reducing injury risk and improving performance.',
        upgrade90Day: `90-Day Push-Up Progression: Week 1-4: Build foundation with standard push-ups (3x max reps, 3x/week) and incline variations (3x15, 2x/week). Week 5-8: Increase difficulty - weighted push-ups (vest or plate, 3x8-10, 2x/week) and decline push-ups (3x10, 2x/week). Week 9-12: Advanced variations - plyometric push-ups (3x6-8, 2x/week) and archer push-ups (3x6 each side, 2x/week). Expect to double your max reps and significantly improve upper body strength.`,
        upgrade14Day: `14-Day Push-Up Intensive: Days 1-7: Daily push-ups (3x max reps) focusing on perfect form. Add incline push-ups (3x15) every other day. Include shoulder mobility work (5 min daily). Days 8-14: Increase volume - push-ups (4x max reps) 5x this week. Add diamond push-ups (3x8) 3x this week. Continue mobility work. You'll notice improved strength and endurance within 2 weeks.`,
        tech: `Record push-ups from the side. Check: 1) Body alignment - is there a straight line from head to heels? 2) Elbow angle - do elbows bend to 90°? 3) Range of motion - does chest touch ground? 4) Core engagement - does lower back sag? Use video analysis to identify form breakdowns. Track max reps weekly. Apps like Push-Up Counter can help track progress and maintain proper tempo.`
      }
    };
  }

  // High Knee March Analysis
  if (testId === 'highknee') {
    return {
      areasOfImprovement: ['Hip flexor strength', 'Knee lift height', 'Balance and coordination'],
      suggestions: ['Practice high knees daily', 'Strengthen hip flexors', 'Improve core stability'],
      potentialRisks: ['Hip flexor strain', 'Lower back compensation', 'Knee stress'],
      title: 'Boost Your Hip Power! 🏃',
      science: {
        explanation: `High knee march tests hip flexor strength, core stability, and lower body coordination. Strong hip flexors are crucial for running, jumping, and explosive movements in ${sport}. This exercise activates the iliopsoas, rectus femoris, and core muscles while improving dynamic balance.`,
        toDoFix: `Practice high knees 3x per week: 3 sets of 20 reps (10 each leg). Focus on: 1) Knee height - aim for 90° hip flexion, 2) Upright posture - engage core, don't lean back, 3) Controlled tempo - 1 sec up, 1 sec down, 4) Balance - maintain stability on standing leg. Progress by adding resistance bands or increasing speed.`,
        drill: `High Knee Progression: Start with slow, controlled marches (3x20). Progress to faster tempo (3x30). Add resistance band around thighs (3x15). Finally, explosive high knees (3x10). Perform 3x per week. This builds hip flexor strength and power progressively.`
      },
      sport: {
        application: `Strong hip flexors improve running speed, agility, and explosive movements in ${sport}. High knee strength translates to better acceleration, higher jumps, and quicker direction changes. Essential for any sport requiring speed and power.`,
        upgrade90Day: `90-Day Hip Power Program: Weeks 1-4: Build baseline with slow high knees (3x20, 3x/week). Weeks 5-8: Add resistance bands (3x15, 3x/week) and increase tempo. Weeks 9-12: Explosive high knees (3x10, 3x/week) and single-leg variations. Expect significant improvements in running speed and jumping ability.`,
        upgrade14Day: `14-Day High Knee Challenge: Days 1-7: Daily high knees (3x20) with perfect form. Days 8-14: Increase to 4x25 with faster tempo. Add core work (planks, 3x30sec) daily. You'll notice improved hip strength and running mechanics within 2 weeks.`,
        tech: `Record from the side. Measure knee height (should reach 90° hip angle). Track: 1) Knee lift height, 2) Posture (upright vs leaning), 3) Balance quality, 4) Tempo consistency. Use apps to count reps and maintain rhythm.`
      }
    };
  }

  // Plank Hold Analysis
  if (testId === 'plank') {
    return {
      areasOfImprovement: ['Core endurance', 'Shoulder stability', 'Body alignment'],
      suggestions: ['Build plank duration gradually', 'Strengthen core muscles', 'Improve shoulder stability'],
      potentialRisks: ['Lower back sag', 'Shoulder fatigue', 'Hip drop'],
      title: 'Strengthen Your Core Foundation! 🧘',
      science: {
        explanation: `Plank tests core endurance, shoulder stability, and full-body tension. A strong plank indicates excellent core strength crucial for injury prevention and performance in ${sport}. The plank engages rectus abdominis, transverse abdominis, obliques, shoulders, and glutes simultaneously.`,
        toDoFix: `Build plank endurance 3x per week: Start with 3x20-30sec holds. Focus on: 1) Neutral spine - don't sag or pike hips, 2) Shoulder position - directly over elbows, 3) Full-body tension - squeeze glutes and quads, 4) Breathing - maintain steady breath. Progress by adding 10 seconds weekly. Include side planks (3x20sec each side) for oblique strength.`,
        drill: `Plank Progression: Week 1-2: Standard plank (3x30sec). Week 3-4: Increase to 3x45sec. Week 5-6: Add leg lifts (3x10 each leg). Week 7-8: Plank to push-up transitions (3x8). Perform 3x per week. This builds core endurance systematically.`
      },
      sport: {
        application: `Core strength from planks improves stability, power transfer, and injury prevention in ${sport}. A strong core allows efficient force generation, better balance, and reduced injury risk. Essential for all athletic movements.`,
        upgrade90Day: `90-Day Core Strength Program: Weeks 1-4: Build baseline with standard planks (3x30-60sec, 3x/week). Weeks 5-8: Add variations - side planks (3x30sec), plank jacks (3x15), RKC planks (3x20sec). Weeks 9-12: Advanced planks - weighted planks, plank walks, Copenhagen planks. Expect to triple your hold time and significantly improve core strength.`,
        upgrade14Day: `14-Day Plank Challenge: Days 1-7: Daily planks (3x max hold) with perfect form. Add side planks (2x20sec each side). Days 8-14: Increase to 4x max hold. Add plank variations (shoulder taps, leg lifts). You'll notice improved core endurance and stability within 2 weeks.`,
        tech: `Record from the side. Check: 1) Hip alignment - level with shoulders and ankles, 2) Shoulder position - over elbows, 3) Spine neutrality - no sag or pike. Use timer apps to track hold duration. Monitor form quality throughout the hold.`
      }
    };
  }

  // Forward Bend Analysis
  if (testId === 'forwardbend') {
    return {
      areasOfImprovement: ['Hamstring flexibility', 'Lower back mobility', 'Hip hinge pattern'],
      suggestions: ['Daily stretching routine', 'Improve hamstring flexibility', 'Practice proper hip hinge'],
      potentialRisks: ['Lower back strain', 'Hamstring pull', 'Knee hyperextension'],
      title: 'Unlock Your Flexibility! 🙇',
      science: {
        explanation: `Forward bend tests hamstring flexibility, lower back mobility, and hip hinge mechanics. Good flexibility reduces injury risk and improves movement efficiency in ${sport}. This movement requires hamstring length, hip mobility, and proper spinal mechanics.`,
        toDoFix: `Stretch daily: Forward fold (3x30sec), seated hamstring stretch (3x30sec each leg), and standing toe touch (3x30sec). Focus on: 1) Hip hinge - bend from hips, not lower back, 2) Knee position - slight bend okay, 3) Breathing - exhale into stretch, 4) Gradual progression - don't force it. Perform after workouts when muscles are warm.`,
        drill: `Flexibility Progression: Week 1-2: Gentle forward folds (3x30sec daily). Week 3-4: Increase hold to 45sec. Week 5-6: Add seated hamstring stretches (3x30sec each leg). Week 7-8: Dynamic leg swings (3x10 each leg). Perform daily. This improves flexibility systematically.`
      },
      sport: {
        application: `Flexibility from forward bends improves movement range, reduces injury risk, and enhances performance in ${sport}. Better hamstring flexibility allows deeper squats, faster running, and more efficient movements. Essential for injury prevention.`,
        upgrade90Day: `90-Day Flexibility Program: Weeks 1-4: Daily static stretching (forward fold, hamstring stretch, 3x30sec each). Weeks 5-8: Add dynamic stretching (leg swings, walking lunges with reach). Weeks 9-12: Incorporate yoga flows and PNF stretching. Expect significant improvements in flexibility and movement quality.`,
        upgrade14Day: `14-Day Flexibility Challenge: Days 1-7: Daily forward folds (3x30sec) and hamstring stretches (3x30sec each leg). Days 8-14: Increase to 3x45sec. Add hip flexor stretches. Stretch after every workout. You'll notice improved flexibility and reduced tightness within 2 weeks.`,
        tech: `Record from the side. Measure reach distance (fingertips to floor). Track: 1) Hip hinge quality, 2) Knee position, 3) Spinal curve. Use apps to track flexibility progress over time. Take weekly photos to visualize improvements.`
      }
    };
  }

  // Default analysis for other tests
  const defaultTestName = test.testName || test.testId || 'Exercise';
  
  return {
    areasOfImprovement: ['Form and technique', 'Strength and endurance', 'Body control'],
    suggestions: ['Practice regularly', 'Focus on proper form', 'Progressive overload'],
    potentialRisks: ['Injury from poor form', 'Overtraining without recovery'],
    title: `Improve Your ${defaultTestName}! 💪`,
    science: {
      explanation: `This test measures your physical capabilities in ${defaultTestName}. Continue practicing with proper form to see improvements.`,
      toDoFix: `Practice this movement 3x per week with focus on form and control. Start with easier variations and progress gradually.`,
      drill: `Perform 3 sets of this exercise, 3x per week. Focus on quality over quantity.`
    },
    sport: {
      application: `This movement pattern is important for overall athletic development in ${sport}.`,
      upgrade90Day: `Follow a progressive 90-day program, increasing difficulty every 3-4 weeks.`,
      upgrade14Day: `Practice daily for 14 days, focusing on form and consistency.`,
      tech: `Use video analysis to track your form and progress over time.`
    }
  };
}

function calculatePhysicalScore(testAnalyses) {
  if (testAnalyses.length === 0) return 0;
  
  const totalScore = testAnalyses.reduce((sum, test) => sum + test.score, 0);
  const averageScore = Math.round(totalScore / testAnalyses.length);
  
  return {
    overall: averageScore,
    rating: averageScore >= 90 ? 'Excellent' : 
            averageScore >= 75 ? 'Good' : 
            averageScore >= 60 ? 'Developing' : 'Needs Work',
    testsCompleted: testAnalyses.length
  };
}
